import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Configuration } from '@shared/models/configuration';
import { Store } from '@ngrx/store';
import { SecurityState } from '@shared/store/reducers/security.reducer';
import { PlatformQueryService } from '@core/services/platform-query.service';
import { PlatformMessageBar } from '@shared/models/platform-tool-models';
import { PlatformCommandResponse } from '@shared/commands/command-base';
import { Subject } from 'rxjs';
import { WorkspaceSyncFollower } from '../workspace-sync/workspace-sync-follower';

@Component({
  selector: 'app-dashboard-home-cards',
  templateUrl: './dashboard-home-cards.component.html',
  styleUrls: ['./dashboard-home-cards.component.scss'],
  standalone: false
})
export class DashboardHomeCardsComponent implements OnInit, OnDestroy {

  cards: any[] = [];
  expandedLinks: Set<string> = new Set();

  requireWorkspaceSync: boolean = false;
  workspaceSyncMessageBar: PlatformMessageBar;
  private ngUnsubscribe = new Subject<void>();
  private workspaceSync: WorkspaceSyncFollower;

  constructor(
    private router: Router,
    private securityStore: Store<SecurityState>,
    private platformQueryService: PlatformQueryService
  ) {
    this.workspaceSync = new WorkspaceSyncFollower(securityStore, platformQueryService, this.ngUnsubscribe,
      response => this.showWorkspaceSync(response));
  }

  ngOnInit(): void {
    this.loadNavigationData();
    this.workspaceSync.check();
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }

  /**
   * Load navigation data from Configuration
   */
  private loadNavigationData(): void {
    const navigation = Configuration.getNavigation();

    if (navigation && navigation.length > 0) {
      this.cards = this.transformNavigationToCards(navigation);
    }
  }

  /**
   * Transform navigation data structure to cards structure
   * Filters to show only items under "My Solutions"
   */
  private transformNavigationToCards(navigation: any[]): any[] {
    const cardsArray: any[] = [];
    let cardId = 1;

    // Find "My Solutions" group/collapse item
    navigation.forEach(navItem => {
      const isMySolutions =
        navItem.id === 'my-solutions' ||
        navItem.title === 'My Solutions' ||
        navItem.translate === 'NAV.MENU.MY_SOLUTIONS.TITLE';

      if (isMySolutions && navItem.children) {
        // Extract children from "My Solutions"
        navItem.children.forEach(child => {
          if (!child.hidden) {
            // Each child becomes a card
            if (child.type === 'item') {
              const cardData = {
                id: cardId++,
                title: child.title || child.translate,
                icon: child.icon || 'fa-regular fa-folder',
                isEmoji: false,
                links: [
                  {
                    id: `${cardId}-0`,
                    label: child.title || child.translate,
                    url: child.url || '#',
                    children: []
                  }
                ]
              };
              cardsArray.push(cardData);
            } else if (child.type === 'collapse' && child.children) {
              // If child is collapse, create a card with its children as links
              const cardData = {
                id: cardId++,
                title: child.title || child.translate,
                icon: '', // Will be set from first child
                isEmoji: false,
                links: []
              };

              let linkIndex = 0;
              let firstChildIcon: string | null = null;

              child.children.forEach(grandChild => {
                if (!grandChild.hidden) {
                  // Store first child's icon to use as card icon
                  if (!firstChildIcon && grandChild.icon) {
                    firstChildIcon = grandChild.icon;
                  }

                  // Check if grandChild has children (3rd level)
                  const hasChildren = grandChild.children && grandChild.children.length > 0;
                  const children: any[] = [];

                  if (hasChildren) {
                    grandChild.children.forEach(greatGrandChild => {
                      if (!greatGrandChild.hidden) {
                        children.push({
                          label: greatGrandChild.title || greatGrandChild.translate,
                          url: greatGrandChild.url || '#'
                        });
                      }
                    });
                  }

                  cardData.links.push({
                    id: `${cardData.id}-${linkIndex}`,
                    label: grandChild.title || grandChild.translate,
                    url: grandChild.url || '#',
                    children: children,
                    hasChildren: children.length > 0
                  });
                  linkIndex++;
                }
              });

              // Set card icon to first child's icon, or use default
              cardData.icon = firstChildIcon || 'fa-regular fa-folder';

              if (cardData.links.length > 0) {
                cardsArray.push(cardData);
              }
            }
          }
        });
      }
    });

    return cardsArray;
  }

  /**
   * Toggle link expansion
   */
  toggleLinkExpansion(linkId: string): void {
    if (this.expandedLinks.has(linkId)) {
      this.expandedLinks.delete(linkId);
    } else {
      this.expandedLinks.add(linkId);
    }
  }

  /**
   * Check if link is expanded
   */
  isLinkExpanded(linkId: string): boolean {
    return this.expandedLinks.has(linkId);
  }

  /**
   * Navigate to link
   */
  navigateToLink(url: string): void {
    if (url && url !== '#') {
      this.router.navigate([url]);
    }
  }

  /** Follow the Sync the banner button started (unless cancelled), so it shows without a reload. */
  onWorkspaceSyncCommandFinished(response: PlatformCommandResponse): void {
    this.workspaceSync.onSyncCommandFinished(response);
  }

  private showWorkspaceSync(response: any): void {
    if (response?.fieldValues) {
      this.requireWorkspaceSync = response.fieldValues['RequireWorkspaceSync'];
      this.workspaceSyncMessageBar = response.fieldValues['WorkspaceSyncMessageBar'];
      // The API sends the bar only when there is something to show.
      if (this.workspaceSyncMessageBar) {
        this.workspaceSyncMessageBar.fieldValues['BannerColor'] = '#FCF4D4'; // set background color
      }
      console.log('[DashboardHomeCardsComponent] Workspace sync required:', this.requireWorkspaceSync);
    }
  }
}
